import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { PcRecommendedCallout } from '@/components/PcRecommendedCallout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass, listRowClass, inlineSuccessClass, inlineErrorClass } from '@/components/ui/inputStyles';
import { notifySessionRefresh } from '@/lib/sessionEvents';
import { generateWesWebhookSecret } from '@/lib/webhookSecret';
import { useSession } from '@/hooks/useSession';
import { aiProviderLabel, type UserAiProfile } from '@/lib/aiEngine';

const PER_PAGE = 20;

/** 符合服务端 /^[a-zA-Z0-9_-]{2,64}$/ */
function randomSiteCode(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const n = 10;
  const u = new Uint8Array(n);
  crypto.getRandomValues(u);
  let tail = '';
  for (let i = 0; i < n; i++) tail += alphabet[u[i]! % alphabet.length];
  return `s_${tail}`;
}

type SiteItem = {
  id: number;
  site_code: string;
  site_name: string;
  enabled: boolean;
  created_at?: string;
  /** 非空表示二级站点，值为一级站点 id */
  parent_site_id?: number | null;
  /** 仅系统管理员接口返回 */
  owner_user_id?: number | null;
  owner_username?: string | null;
};

type ListResp = {
  ok: boolean;
  current_site_id: number;
  items: SiteItem[];
  total: number;
  page: number;
  per_page: number;
};

type SiteAuthSettingsResp = {
  ok: boolean;
  site: { id: number; site_code: string; site_name: string };
  jinsuju_webhook_secret: string;
  android_api_key?: string;
  placeholders: { webhook_secret_is_placeholder: boolean; android_api_key_is_placeholder?: boolean };
};

type SystemUserRow = {
  id: number;
  username: string;
  role: string;
  account_enabled: boolean;
  owned_sites_count?: number;
};

type SystemUsersListResp = {
  ok: boolean;
  items: SystemUserRow[];
  total: number;
  page: number;
  per_page: number;
};

export function SitesPage() {
  const { capabilities, role, ownedSitesCount, maxOwnedSites } = useSession();
  const isSysAdmin = capabilities.manage_users;
  const atSiteLimit =
    !isSysAdmin &&
    role === 'user' &&
    typeof ownedSitesCount === 'number' &&
    typeof maxOwnedSites === 'number' &&
    ownedSitesCount >= maxOwnedSites;
  const [items, setItems] = useState<SiteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [currentSiteId, setCurrentSiteId] = useState<number>(0);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [ownerQInput, setOwnerQInput] = useState('');
  const [appliedOwnerQ, setAppliedOwnerQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addCode, setAddCode] = useState('');
  const [addName, setAddName] = useState('');
  const [addParentId, setAddParentId] = useState(0);
  const [parentRootOptions, setParentRootOptions] = useState<SiteItem[]>([]);
  const [parentOptionsLoading, setParentOptionsLoading] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addAiProfiles, setAddAiProfiles] = useState<UserAiProfile[]>([]);
  const [addAiProfileId, setAddAiProfileId] = useState(0);
  const [addAiDefaultId, setAddAiDefaultId] = useState(0);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<SiteItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [authOpen, setAuthOpen] = useState(false);
  const [authSiteId, setAuthSiteId] = useState(0);
  const [authLabel, setAuthLabel] = useState('');
  const [authSecret, setAuthSecret] = useState('');
  const [authAndroidKey, setAuthAndroidKey] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authSaving, setAuthSaving] = useState(false);
  const [authInlineMsg, setAuthInlineMsg] = useState('');
  const [authInlineErr, setAuthInlineErr] = useState('');

  const [shareOpen, setShareOpen] = useState(false);
  const [shareSiteId, setShareSiteId] = useState(0);
  const [shareLabel, setShareLabel] = useState('');
  const [sharePath, setSharePath] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareUploadBusy, setShareUploadBusy] = useState(false);
  const [shareInlineMsg, setShareInlineMsg] = useState('');
  const [shareInlineErr, setShareInlineErr] = useState('');

  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateSite, setMigrateSite] = useState<SiteItem | null>(null);
  const [migrateUsers, setMigrateUsers] = useState<SystemUserRow[]>([]);
  const [migrateUsersLoading, setMigrateUsersLoading] = useState(false);
  const [migrateUserQ, setMigrateUserQ] = useState('');
  const [migrateTargetId, setMigrateTargetId] = useState(0);
  const [migrateSaving, setMigrateSaving] = useState(false);
  const [migrateErr, setMigrateErr] = useState('');

  const load = useCallback(
    async (override?: { page?: number; q?: string; ownerQ?: string }) => {
      const p = override?.page ?? page;
      const qRaw = override?.q !== undefined ? override.q : appliedQ;
      const q = qRaw.trim();
      const ownerRaw = override?.ownerQ !== undefined ? override.ownerQ : appliedOwnerQ;
      const ownerQ = isSysAdmin ? ownerRaw.trim() : '';
      setErr('');
      const r = await apiFetch<ListResp>('list_sites', {
        method: 'GET',
        query: {
          page: p,
          per_page: PER_PAGE,
          q: q || undefined,
          ...(isSysAdmin && ownerQ !== '' ? { owner_q: ownerQ } : {}),
        },
      });
      if (r.ok) {
        const tp = Math.max(1, Math.ceil(r.total / r.per_page));
        const safePage = Math.min(p, tp);
        if (safePage !== p) {
          setPage(safePage);
          return;
        }
        setPage(safePage);
        setItems(r.items);
        setTotal(r.total);
        setCurrentSiteId(r.current_site_id);
      } else {
        setErr('加载站点列表失败');
      }
    },
    [page, appliedQ, appliedOwnerQ, isSysAdmin]
  );

  useEffect(() => {
    void load();
  }, [load]);

  function runSearch() {
    setAppliedQ(qInput.trim());
    if (isSysAdmin) {
      setAppliedOwnerQ(ownerQInput.trim());
    }
    setPage(1);
  }

  async function openAddModal() {
    setAddCode('');
    setAddName('');
    setAddParentId(0);
    setAddAiProfileId(0);
    setAddOpen(true);
    setParentOptionsLoading(true);
    try {
      const [sitesR, aiR] = await Promise.all([
        apiFetch<ListResp>('list_sites', {
          method: 'GET',
          query: { page: 1, per_page: 200, only_roots: 1 },
        }),
        apiFetch<{
          ok: boolean;
          profiles?: UserAiProfile[];
          default_profile_id?: number;
        }>('list_user_ai_profiles', { method: 'GET' }),
      ]);
      setParentRootOptions(sitesR.ok ? sitesR.items : []);
      const profiles = aiR.ok ? (aiR.profiles ?? []) : [];
      setAddAiProfiles(profiles);
      const defId = aiR.ok ? (aiR.default_profile_id ?? 0) : 0;
      setAddAiDefaultId(defId);
      setAddAiProfileId(0);
    } catch {
      setParentRootOptions([]);
      setAddAiProfiles([]);
      setAddAiDefaultId(0);
    } finally {
      setParentOptionsLoading(false);
    }
  }

  async function submitAdd() {
    setAddSaving(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>('create_site', {
        method: 'POST',
        body: JSON.stringify({
          site_code: addCode.trim(),
          site_name: addName.trim(),
          ...(addParentId > 0 ? { parent_site_id: addParentId } : {}),
          ...(addAiProfileId > 0 ? { ai_profile_id: addAiProfileId } : { use_default_ai: true }),
        }),
      });
      if (!r.ok) {
        if (r.err === 'site_limit_reached') {
          setErr(r.detail || `已达到站点数量上限（${maxOwnedSites ?? ''} 个）`);
          notifySessionRefresh();
          return;
        }
        setErr('创建站点失败：' + (r.err || 'unknown'));
        return;
      }
      setAddOpen(false);
      setMsg('站点已创建');
      setAppliedQ('');
      setQInput('');
      notifySessionRefresh();
      await load({ page: 1, q: '' });
    } finally {
      setAddSaving(false);
    }
  }

  function openEditModal(it: SiteItem) {
    setEditRow(it);
    setEditName(it.site_name);
    setEditOpen(true);
  }

  async function submitEdit() {
    if (!editRow || !editName.trim()) return;
    setEditSaving(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('update_site', {
        method: 'POST',
        body: JSON.stringify({ site_id: editRow.id, site_name: editName.trim() }),
      });
      if (!r.ok) {
        setErr('保存失败：' + (r.err || 'unknown'));
        return;
      }
      setEditOpen(false);
      setEditRow(null);
      setMsg('站点名称已更新');
      notifySessionRefresh();
      await load();
    } finally {
      setEditSaving(false);
    }
  }

  const loadAuthForSite = useCallback(async (siteId: number) => {
    if (siteId < 1) return;
    setAuthLoading(true);
    setAuthInlineErr('');
    setAuthSecret('');
    setAuthAndroidKey('');
    try {
      const r = await apiFetch<SiteAuthSettingsResp>('get_site_auth_settings', {
        method: 'GET',
        query: { site_id: siteId },
      });
      if (r.ok) {
        setAuthLabel(`${r.site.site_name}（${r.site.site_code}）`);
        setAuthSecret(r.jinsuju_webhook_secret || '');
        setAuthAndroidKey((r.android_api_key ?? '').trim());
      } else {
        setAuthInlineErr('加载密钥失败');
      }
    } catch {
      setAuthInlineErr('加载密钥失败');
    } finally {
      setAuthLoading(false);
    }
  }, []);

  async function openAuthModal(it: SiteItem) {
    setAuthSiteId(it.id);
    setAuthInlineMsg('');
    setAuthInlineErr('');
    setAuthOpen(true);
    await loadAuthForSite(it.id);
  }

  function fillRandomWebhookSecret() {
    setAuthSecret(generateWesWebhookSecret());
    setAuthInlineErr('');
    setAuthInlineMsg('已填入新密钥，请点击「保存」生效');
  }

  function fillRandomAndroidKey() {
    setAuthAndroidKey(generateWesWebhookSecret());
    setAuthInlineErr('');
    setAuthInlineMsg('已填入新 Android 密钥，请点击「保存」生效');
  }

  const loadShareForSite = useCallback(async (siteId: number) => {
    if (siteId < 1) return;
    setShareLoading(true);
    setShareInlineErr('');
    setShareInlineMsg('');
    setSharePath('');
    try {
      const r = await apiFetch<{ ok: boolean; minipro_home_share_image_path?: string; err?: string }>(
        'get_site_content_settings',
        { method: 'GET', query: { site_id: siteId } }
      );
      if (r.ok) {
        setSharePath(r.minipro_home_share_image_path ?? '');
      } else {
        setShareInlineErr('加载失败');
      }
    } catch {
      setShareInlineErr('加载失败');
    } finally {
      setShareLoading(false);
    }
  }, []);

  async function openShareModal(it: SiteItem) {
    setShareSiteId(it.id);
    setShareLabel(`${it.site_name}（${it.site_code}）`);
    setShareOpen(true);
    setShareInlineMsg('');
    setShareInlineErr('');
    await loadShareForSite(it.id);
  }

  async function uploadMiniproHomeShare(file: File) {
    if (shareSiteId < 1) return;
    setShareUploadBusy(true);
    setShareInlineErr('');
    setShareInlineMsg('');
    try {
      const fd = new FormData();
      fd.append('site_id', String(shareSiteId));
      fd.append('slot', 'minipro_home_share');
      fd.append('file', file);
      const url = apiUrl('upload_newspic_custom_asset', {});
      const res = await fetch(url, { method: 'POST', body: fd, credentials: 'include' });
      const text = (await res.text()).replace(/^\uFEFF/, '').trim();
      const parsed = JSON.parse(text) as { ok?: boolean; local_path?: string; err?: string };
      if (parsed.ok && parsed.local_path) {
        setSharePath(parsed.local_path);
        setShareInlineMsg('已上传，请点击「保存」写入数据库');
      } else {
        setShareInlineErr(parsed.err || '上传失败');
      }
    } catch {
      setShareInlineErr('上传失败');
    } finally {
      setShareUploadBusy(false);
    }
  }

  async function submitShareImage() {
    if (shareSiteId < 1) return;
    setShareSaving(true);
    setShareInlineErr('');
    setShareInlineMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_site_minipro_home_share_image', {
        method: 'POST',
        body: JSON.stringify({
          site_id: shareSiteId,
          minipro_home_share_image_path: sharePath,
        }),
      });
      if (!r.ok) {
        setShareInlineErr(r.err === 'site_settings_not_found' ? '站点设置缺失，请先在「内容与样式」保存一次' : '保存失败');
        return;
      }
      setShareInlineMsg('已保存');
      setMsg('小程序首页转发图已更新');
    } catch {
      setShareInlineErr('保存失败');
    } finally {
      setShareSaving(false);
    }
  }

  async function submitAuth() {
    if (authSiteId < 1) return;
    setAuthSaving(true);
    setAuthInlineMsg('');
    setAuthInlineErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('save_site_auth_settings', {
        method: 'POST',
        body: JSON.stringify({
          site_id: authSiteId,
          jinsuju_webhook_secret: authSecret.trim(),
          android_api_key: authAndroidKey.trim(),
        }),
      });
      if (!r.ok) {
        setAuthInlineErr('保存失败');
        return;
      }
      await loadAuthForSite(authSiteId);
      setAuthInlineMsg('密钥已保存，可关闭本窗口。');
      setMsg('站点密钥已更新');
    } catch {
      setAuthInlineErr('保存失败');
    } finally {
      setAuthSaving(false);
    }
  }

  async function switchSite(siteId: number) {
    setErr('');
    const r = await apiFetch<{ ok: boolean }>('switch_site', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId }),
    });
    if (r.ok) {
      setCurrentSiteId(siteId);
      setMsg('已切换站点');
      notifySessionRefresh();
    } else {
      setErr('切换站点失败');
    }
  }

  async function toggleEnabled(siteId: number, enabled: boolean) {
    setErr('');
    const name = items.find((x) => x.id === siteId)?.site_name?.trim() || '';
    if (name === '') {
      setErr('站点名称缺失，请刷新后重试');
      return;
    }
    const r = await apiFetch<{ ok: boolean; err?: string }>('update_site', {
      method: 'POST',
      body: JSON.stringify({ site_id: siteId, site_name: name, enabled }),
    });
    if (!r.ok) {
      const detail =
        r.err === 'site_not_found'
          ? '站点不存在'
          : r.err === 'site_disabled'
            ? '站点已禁用，请先在站点管理中启用'
            : r.err === 'forbidden_site'
              ? '无权操作该站点'
              : '';
      setErr(detail !== '' ? `更新站点状态失败：${detail}` : '更新站点状态失败');
      return;
    }
    setMsg(enabled ? '站点已启用' : '站点已禁用');
    notifySessionRefresh();
    await load();
  }

  const loadMigrateUsers = useCallback(async (q: string) => {
    setMigrateUsersLoading(true);
    setMigrateErr('');
    try {
      const r = await apiFetch<SystemUsersListResp>('list_system_users', {
        method: 'GET',
        query: { page: 1, per_page: 100, q: q.trim() || undefined },
      });
      if (r.ok) {
        setMigrateUsers(r.items);
      } else {
        setMigrateErr('加载用户列表失败');
      }
    } catch {
      setMigrateErr('加载用户列表失败');
    } finally {
      setMigrateUsersLoading(false);
    }
  }, []);

  function openMigrateModal(it: SiteItem) {
    setMigrateSite(it);
    setMigrateTargetId(0);
    setMigrateUserQ('');
    setMigrateErr('');
    setMigrateOpen(true);
    void loadMigrateUsers('');
  }

  async function submitMigrate() {
    if (!migrateSite || migrateTargetId < 1) {
      setMigrateErr('请选择目标账号');
      return;
    }
    const curOwner = migrateSite.owner_user_id ?? null;
    if (curOwner !== null && curOwner === migrateTargetId) {
      setMigrateErr('目标账号与当前归属相同');
      return;
    }
    const ok = window.confirm(
      `将站点「${migrateSite.site_name}」（${migrateSite.site_code}）整站迁移到用户 ID ${migrateTargetId}。\n\n` +
        '站点下的条目、图片、金数据 Webhook 密钥与字段映射等配置均随站点 ID 保留，无需在金数据侧改地址。\n' +
        '原属账号名下小程序中若勾选了本站点，将自动从该小程序配置中移除本站点。\n\n确定执行？'
    );
    if (!ok) return;
    setMigrateSaving(true);
    setMigrateErr('');
    setErr('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        new_owner_username?: string;
        miniprograms_site_removed?: number;
      }>('migrate_site_owner', {
        method: 'POST',
        body: JSON.stringify({ site_id: migrateSite.id, new_owner_user_id: migrateTargetId }),
      });
      if (!r.ok) {
        const d = r.detail ? `${r.err || 'failed'}：${r.detail}` : r.err || '迁移失败';
        setMigrateErr(d);
        return;
      }
      const n = typeof r.miniprograms_site_removed === 'number' ? r.miniprograms_site_removed : 0;
      setMigrateOpen(false);
      setMigrateSite(null);
      setMsg(
        `站点已迁移至 ${r.new_owner_username || '目标用户'}（ID ${migrateTargetId}）。` +
          (n > 0 ? ` 已从 ${n} 个原属账号小程序配置中移除该站点。` : '')
      );
      notifySessionRefresh();
      await load();
    } catch {
      setMigrateErr('请求失败');
    } finally {
      setMigrateSaving(false);
    }
  }

  async function removeSite(it: SiteItem) {
    if (
      !window.confirm(
        `确定删除站点「${it.site_name}」（${it.site_code}）？\n将同时删除该站点下的条目、图片、配置与流水线日志，且不可恢复。`
      )
    ) {
      return;
    }
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; current_site_id?: number }>('delete_site', {
        method: 'POST',
        body: JSON.stringify({ site_id: it.id }),
      });
      if (!r.ok) {
        setErr('删除站点失败');
        return;
      }
      setMsg('站点已删除');
      if (typeof r.current_site_id === 'number') {
        setCurrentSiteId(r.current_site_id);
      }
      notifySessionRefresh();
      await load();
    } catch {
      setErr('删除站点失败');
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  /** 当前页内：一级站点与其二级子站分组展示；跨页导致的「孤儿」子站附在列表末尾并注明父级。 */
  const siteRowsForDisplay = useMemo(() => {
    const byId = new Map(items.map((x) => [x.id, x]));
    const ordered: Array<{ site: SiteItem; depth: 0 | 1; subline?: string }> = [];
    const seen = new Set<number>();
    const roots = items.filter((x) => !x.parent_site_id).sort((a, b) => a.id - b.id);
    const childrenOf = (pid: number) =>
      items.filter((x) => x.parent_site_id === pid).sort((a, b) => a.id - b.id);
    for (const r of roots) {
      ordered.push({ site: r, depth: 0 });
      seen.add(r.id);
      for (const c of childrenOf(r.id)) {
        ordered.push({ site: c, depth: 1, subline: `二级站点 · 隶属「${r.site_name}」` });
        seen.add(c.id);
      }
    }
    for (const x of [...items].sort((a, b) => a.id - b.id)) {
      if (seen.has(x.id)) continue;
      const pid = x.parent_site_id;
      const parent = pid ? byId.get(pid) : undefined;
      const subline = pid
        ? parent
          ? `二级站点 · 隶属「${parent.site_name}」（父级在本页上方）`
          : `二级站点 · 父站点 ID ${pid}（本列表页未展示，可调整筛选或翻页查看）`
        : undefined;
      ordered.push({ site: x, depth: pid ? 1 : 0, subline });
      seen.add(x.id);
    }
    return ordered;
  }, [items]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="站点管理"
        description="多站点数据相互隔离。列表中的「站点 ID」为数字主键（小程序页面路径、接口 site_id 均用此 ID，勿用 site_code）。字段映射与正文模板在「内容与样式」中按站点配置。"
      />

      <PcRecommendedCallout>
        对接密钥、Webhook 地址等需要频繁复制粘贴，建议在电脑浏览器中配置与核对。
      </PcRecommendedCallout>

      <Card
        title="站点列表"
        titleExtra={
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={atSiteLimit}
            title={atSiteLimit ? `已达上限（${maxOwnedSites ?? ''} 个站点）` : undefined}
            onClick={openAddModal}
          >
            添加
          </Button>
        }
      >
        {atSiteLimit ? (
          <p className="mb-3 rounded-xl border border-amber-200/90 bg-amber-50/95 px-3 py-2 text-sm text-amber-950">
            当前账号自建站点已达上限（{ownedSitesCount}/{maxOwnedSites}）。如需提高上限，请联系系统管理员。
          </p>
        ) : null}
        {!isSysAdmin && role === 'user' && typeof maxOwnedSites === 'number' ? (
          <p className="mb-3 text-xs text-slate-500">
            普通用户最多自建 {maxOwnedSites} 个站点（当前 {ownedSitesCount ?? 0} 个）。
          </p>
        ) : null}
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className={`${labelClass} flex-1`}>
              <span>{isSysAdmin ? '关键词（站点标识 / 名称 / ID）' : '关键词'}</span>
              <input
                className={inputClass}
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
                placeholder={isSysAdmin ? '可选' : '站点标识、名称或 ID'}
              />
            </label>
            {isSysAdmin ? (
              <label className={`${labelClass} flex-1`}>
                <span>所属账号（用户名或用户 ID）</span>
                <input
                  className={inputClass}
                  value={ownerQInput}
                  onChange={(e) => setOwnerQInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runSearch();
                  }}
                  placeholder="可选，与上方关键词同时生效时为「且」"
                />
              </label>
            ) : null}
            <div className="flex gap-2 sm:shrink-0">
              <Button variant="secondary" onClick={runSearch}>
                查询
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setQInput('');
                  setAppliedQ('');
                  setOwnerQInput('');
                  setAppliedOwnerQ('');
                  setPage(1);
                }}
              >
                重置
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">暂无站点数据</p>
          ) : (
            siteRowsForDisplay.map(({ site: it, depth, subline }) => (
              <div
                key={it.id}
                className={`${listRowClass} ${depth === 1 ? 'ml-4 border-l-2 border-l-emerald-400/90 bg-emerald-50/35 pl-4' : 'border-l-2 border-l-transparent'}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    {depth === 0 ? (
                      <span className="rounded-md bg-slate-800 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                        一级
                      </span>
                    ) : (
                      <span className="rounded-md bg-emerald-700 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white">
                        二级
                      </span>
                    )}
                    <span
                      className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-slate-800"
                      title="数据库站点 ID（小程序 site_id / branch_site_id 用数字）"
                    >
                      ID {it.id}
                    </span>
                    <div className="font-medium text-slate-900">{it.site_name}</div>
                  </div>
                  {subline ? <div className="mt-1 text-xs text-emerald-900/85">{subline}</div> : null}
                  <div className="mt-1 text-xs text-slate-500">
                    标识 {it.site_code} · {it.enabled ? '启用' : '禁用'}
                    {depth === 1 && it.parent_site_id ? (
                      <>
                        {' '}
                        · 父站 ID {it.parent_site_id}
                        <span className="text-slate-400">
                          {' '}
                          （小程序路径示例：site_id={it.parent_site_id}&branch_site_id={it.id}）
                        </span>
                      </>
                    ) : depth === 0 ? (
                      <span className="text-slate-400"> （一级站小程序路径 site_id={it.id}）</span>
                    ) : null}
                    {it.created_at ? ` · 创建于 ${it.created_at}` : ''}
                  </div>
                  {isSysAdmin ? (
                    <div className="mt-1 text-xs text-slate-600">
                      <span className="font-medium text-slate-700">所属账号：</span>
                      {it.owner_username
                        ? `${it.owner_username}（用户 ID ${it.owner_user_id ?? '—'}）`
                        : '系统站点（未绑定用户）'}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEditModal(it)}>
                    编辑
                  </Button>
                  <Button size="sm" variant={it.enabled ? 'secondary' : 'primary'} onClick={() => void toggleEnabled(it.id, !it.enabled)}>
                    {it.enabled ? '禁用' : '启用'}
                  </Button>
                  <Button size="sm" variant={it.id === currentSiteId ? 'secondary' : 'primary'} onClick={() => void switchSite(it.id)}>
                    {it.id === currentSiteId ? '当前站点' : '切换'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => void openAuthModal(it)}>
                    修改密钥
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => void openShareModal(it)}>
                    首页分享图
                  </Button>
                  {isSysAdmin && it.site_code !== 'default' ? (
                    <Button size="sm" variant="secondary" onClick={() => openMigrateModal(it)}>
                      迁移
                    </Button>
                  ) : null}
                  <Button size="sm" variant="danger" onClick={() => void removeSite(it)}>
                    删除
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          summary={total > 0 ? `共 ${total} 条，每页 ${PER_PAGE} 条` : undefined}
        />
      </Card>

      {msg ? <div className={inlineSuccessClass}>{msg}</div> : null}
      {err ? <div className={inlineErrorClass}>{err}</div> : null}

      <Modal
        open={addOpen}
        title="添加站点"
        onClose={() => !addSaving && setAddOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" disabled={addSaving} onClick={() => setAddOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={addSaving || !addCode.trim() || !addName.trim()}
              onClick={() => void submitAdd()}
            >
              {addSaving ? '创建中…' : '确定'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <label className={labelClass}>
            <span>站点标识（site_code）</span>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <input
                className={inputClass}
                value={addCode}
                onChange={(e) => setAddCode(e.target.value)}
                placeholder="如: hz_main"
                spellCheck={false}
              />
              <Button
                type="button"
                variant="secondary"
                className="shrink-0 sm:w-36"
                disabled={addSaving}
                onClick={() => setAddCode(randomSiteCode())}
              >
                随机生成
              </Button>
            </div>
            <span className="mt-1 block text-[11px] font-normal text-slate-500">
              2～64 位字母、数字、下划线或连字符；用于在本系统中区分不同站点。
            </span>
          </label>
          <label className={labelClass}>
            <span>站点名称</span>
            <input className={inputClass} value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="如: 杭州主站" />
          </label>
          <label className={labelClass}>
            <span>站点层级</span>
            <select
              className={inputClass}
              value={addParentId}
              disabled={addSaving || parentOptionsLoading}
              onChange={(e) => setAddParentId(Number(e.target.value) || 0)}
            >
              <option value={0}>作为一级站点</option>
              {parentRootOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  作为二级站点，归属「{p.site_name}」（父站 ID {p.id}）
                </option>
              ))}
            </select>
            <span className="mt-1 block text-[11px] font-normal text-slate-500">
              小程序端会在一级下展示「全部」与子站点筛选；无子站点的一级站会在选择器中合并为一格。
            </span>
          </label>
          {parentOptionsLoading ? <p className="text-xs text-slate-500">正在加载可选的一级站点…</p> : null}

          <div className="rounded-xl border border-slate-200/90 bg-slate-50/50 p-4">
            <p className="mb-2 text-sm font-medium text-slate-800">AI 接口（可选）</p>
            {addAiProfiles.length === 0 ? (
              <p className="text-sm text-amber-900">
                尚未配置 AI 接口，创建后 AI 写标题不可用。
                <Link to="/ai-engines" className="ml-1 font-medium text-teal-800 underline" onClick={() => setAddOpen(false)}>
                  去配置 AI 接口引擎
                </Link>
              </p>
            ) : (
              <label className={labelClass}>
                <span>本站使用</span>
                <select
                  className={inputClass}
                  value={addAiProfileId}
                  disabled={addSaving}
                  onChange={(e) => setAddAiProfileId(Number(e.target.value) || 0)}
                >
                  <option value={0}>
                    账号默认
                    {addAiDefaultId > 0
                      ? `（${addAiProfiles.find((p) => p.id === addAiDefaultId)?.label ?? ''}）`
                      : ''}
                  </option>
                  {addAiProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} · {aiProviderLabel(String(p.ai_provider))}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={editOpen}
        title="编辑站点"
        onClose={() => !editSaving && setEditOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" disabled={editSaving} onClick={() => setEditOpen(false)}>
              取消
            </Button>
            <Button type="button" variant="primary" disabled={editSaving || !editName.trim()} onClick={() => void submitEdit()}>
              {editSaving ? '保存中…' : '保存'}
            </Button>
          </div>
        }
      >
        {editRow ? (
          <div className="space-y-4">
            <label className={labelClass}>
              <span>站点 ID（数字，对接与小程序路径用）</span>
              <input className={inputClass + ' bg-slate-50 font-mono text-slate-600'} readOnly value={String(editRow.id)} />
            </label>
            <label className={labelClass}>
              <span>站点标识（site_code）</span>
              <input className={inputClass + ' bg-slate-50 text-slate-600'} readOnly value={editRow.site_code} />
            </label>
            {editRow.parent_site_id ? (
              <p className="text-xs text-slate-500">
                二级站点：小程序路径中 <code className="rounded bg-slate-100 px-1">site_id</code> 填父站{' '}
                <span className="font-mono font-semibold">{editRow.parent_site_id}</span>，
                <code className="rounded bg-slate-100 px-1">branch_site_id</code> 填本站{' '}
                <span className="font-mono font-semibold">{editRow.id}</span>。
              </p>
            ) : (
              <p className="text-xs text-slate-500">
                一级站点：小程序路径 <code className="rounded bg-slate-100 px-1">site_id</code> 填{' '}
                <span className="font-mono font-semibold">{editRow.id}</span> 即可。
              </p>
            )}
            <label className={labelClass}>
              <span>站点名称</span>
              <input className={inputClass} value={editName} onChange={(e) => setEditName(e.target.value)} />
            </label>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={migrateOpen}
        title="迁移站点归属"
        wide
        onClose={() => !migrateSaving && setMigrateOpen(false)}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" disabled={migrateSaving} onClick={() => setMigrateOpen(false)}>
              取消
            </Button>
            <Button type="button" variant="primary" disabled={migrateSaving || migrateTargetId < 1} onClick={() => void submitMigrate()}>
              {migrateSaving ? '执行中…' : '确认迁移'}
            </Button>
          </div>
        }
      >
        {migrateSite ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              站点：<span className="font-semibold">{migrateSite.site_name}</span>（{migrateSite.site_code}，ID {migrateSite.id}）
            </p>
            <p className="text-xs leading-relaxed text-slate-600">
              整站数据（信息登记、图片、站点设置、Webhook 密钥、公众号与流水线相关数据等）均绑定在站点 ID 上，随归属账号一并转移；无需在金数据修改推送
              URL。新属主需有接收能力：普通用户迁移入时会占用其「自建站点」名额。
            </p>
            <p className="text-xs text-slate-600">
              当前归属：
              {migrateSite.owner_username
                ? `${migrateSite.owner_username}（用户 ID ${migrateSite.owner_user_id ?? '—'}）`
                : '系统站点（未绑定用户）'}
            </p>
            <div>
              <label className={labelClass}>筛选目标用户（用户名）</label>
              <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                <input
                  className={inputClass}
                  value={migrateUserQ}
                  onChange={(e) => setMigrateUserQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void loadMigrateUsers(migrateUserQ);
                  }}
                  placeholder="可选，回车或点击搜索"
                  disabled={migrateSaving}
                />
                <Button type="button" variant="secondary" className="shrink-0" disabled={migrateSaving} onClick={() => void loadMigrateUsers(migrateUserQ)}>
                  搜索用户
                </Button>
              </div>
            </div>
            <div>
              <label className={labelClass}>目标账号</label>
              {migrateUsersLoading ? (
                <p className="text-sm text-slate-500">加载用户列表…</p>
              ) : (
                <select
                  className={inputClass}
                  value={migrateTargetId > 0 ? String(migrateTargetId) : ''}
                  onChange={(e) => setMigrateTargetId(Number(e.target.value) || 0)}
                  disabled={migrateSaving}
                >
                  <option value="">请选择…</option>
                  {migrateUsers
                    .filter((u) => u.account_enabled)
                    .filter((u) => (migrateSite.owner_user_id != null ? u.id !== migrateSite.owner_user_id : true))
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.username}（ID {u.id}，{u.role === 'admin' ? '管理员' : '用户'}
                        {typeof u.owned_sites_count === 'number' ? `，已有站点 ${u.owned_sites_count}` : ''}）
                      </option>
                    ))}
                </select>
              )}
              <p className="mt-1 text-[11px] text-slate-500">最多列出 100 条匹配用户；更多请用「搜索用户」缩小范围。</p>
            </div>
            {migrateErr ? <div className={inlineErrorClass}>{migrateErr}</div> : null}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={shareOpen}
        title="小程序：首页转发分享图"
        wide
        onClose={() => !shareSaving && !shareUploadBusy && setShareOpen(false)}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="max-w-xl text-xs text-slate-500">
              用户在小程序首页按该站点筛选转发时优先用此图；留空则使用「小程序管理」里的全局配图。与「内容与样式」中的配置为同一字段。
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" type="button" disabled={shareSaving || shareUploadBusy} onClick={() => setShareOpen(false)}>
                关闭
              </Button>
              <Button type="button" variant="primary" disabled={shareSaving || shareLoading || shareSiteId < 1} onClick={() => void submitShareImage()}>
                {shareSaving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            站点：<span className="font-medium text-slate-900">{shareLabel || (shareSiteId > 0 ? `ID ${shareSiteId}` : '—')}</span>
          </p>
          {shareLoading ? (
            <p className="text-xs text-slate-500">加载中…</p>
          ) : (
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
              <label className={labelClass + ' min-w-0'}>
                <span>上传图片（建议 5:4）</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className={inputClass}
                  disabled={shareUploadBusy || shareSiteId < 1}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void uploadMiniproHomeShare(f);
                  }}
                />
              </label>
              {shareUploadBusy ? <span className="text-xs text-slate-500">上传中…</span> : null}
            </div>
          )}
          {sharePath ? (
            <div className="flex flex-wrap items-center gap-2">
              <img
                src={resolveMediaUrl(sharePath)}
                alt=""
                className="h-24 w-[120px] rounded-lg border border-slate-200 object-cover"
              />
              <code className="max-w-[280px] truncate text-[10px] text-slate-500">{sharePath}</code>
              <button
                type="button"
                className="text-xs text-rose-600 hover:underline"
                disabled={shareSaving || shareUploadBusy}
                onClick={() => {
                  setSharePath('');
                  setShareInlineMsg('已清除，请点击「保存」生效');
                  setShareInlineErr('');
                }}
              >
                清除
              </button>
            </div>
          ) : !shareLoading ? (
            <p className="text-xs text-slate-500">未配置（使用小程序全局图）</p>
          ) : null}
          {shareInlineMsg ? <div className={inlineSuccessClass}>{shareInlineMsg}</div> : null}
          {shareInlineErr ? <div className={inlineErrorClass}>{shareInlineErr}</div> : null}
        </div>
      </Modal>

      <Modal
        open={authOpen}
        title="站点密钥（金数据 Webhook / Android 机器人）"
        wide
        onClose={() => !authSaving && setAuthOpen(false)}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">
              金数据 Webhook 与站点级 Android 密钥可分别配置；兼容旧版机器人端使用站点 android_key。新版推荐在「机器人管理」对应小程序中配置小程序级密钥。
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" type="button" disabled={authSaving} onClick={() => setAuthOpen(false)}>
                关闭
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={authSaving || authLoading || authSiteId < 1}
                onClick={() => void submitAuth()}
              >
                {authSaving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            站点：<span className="font-medium text-slate-900">{authLabel || (authSiteId > 0 ? `ID ${authSiteId}` : '—')}</span>
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className={labelClass + ' min-w-0 flex-1'}>
              <span>密钥</span>
              <input
                className={inputClass + ' font-mono text-sm'}
                value={authSecret}
                onChange={(e) => setAuthSecret(e.target.value)}
                placeholder="粘贴或生成"
                spellCheck={false}
                autoComplete="off"
                maxLength={255}
                disabled={authLoading || authSaving}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 sm:mb-0.5"
              disabled={authLoading || authSaving}
              onClick={() => fillRandomWebhookSecret()}
            >
              生成随机密钥
            </Button>
          </div>
          <p className="text-xs text-slate-500">可点击「生成随机密钥」更换；保存后生效。长度不超过 255 字符。</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className={labelClass + ' min-w-0 flex-1'}>
              <span>Android 机器人密钥（api.php 请求 android_key / 请求头 X-Bee-Android-Key）</span>
              <input
                className={inputClass + ' font-mono text-sm'}
                value={authAndroidKey}
                onChange={(e) => setAuthAndroidKey(e.target.value)}
                placeholder="与微推广机器人 App 中「Android API 密钥」一致"
                spellCheck={false}
                autoComplete="off"
                maxLength={255}
                disabled={authLoading || authSaving}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              className="shrink-0 sm:mb-0.5"
              disabled={authLoading || authSaving}
              onClick={() => fillRandomAndroidKey()}
            >
              生成 Android 密钥
            </Button>
          </div>
          <p className="text-xs text-slate-500">可与金数据 Webhook 密钥相同或不同；留空则 Android 鉴权失败，请填写后保存。</p>
          {authLoading ? <p className="text-xs text-slate-500">加载中…</p> : null}
          {authInlineMsg ? (
            <div role="status" className={inlineSuccessClass}>
              {authInlineMsg}
            </div>
          ) : null}
          {authInlineErr ? (
            <div role="alert" className={inlineErrorClass}>
              {authInlineErr}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
